import { MealsService } from "@/clients/services/MealsService";
import { QueryKeys } from "@/constants/query-keys";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";

interface UseGetScaledServingsParams {
  mealId: string;
  servingSize: number;
  enabled?: boolean;
  source?: "plan" | "latest";
  targetDate?: string;
}

/**
 * Hook to fetch scaled ingredient quantities for a meal based on serving size
 */
export const useGetScaledServings = ({
  mealId,
  servingSize,
  enabled = true,
  source,
  targetDate,
}: UseGetScaledServingsParams) => {
  const user = guardGetUserFromLocalStorage();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.SCALED_SERVINGS, mealId, servingSize, source, targetDate ?? ""],
    queryFn: async () => {
      const response = await MealsService.getScaledServingsApiV1MealsMealIdServingsServingSizeGet(
        mealId,
        servingSize,
        user.id,
        undefined,
        undefined,
        undefined,
        source,
        targetDate,
      );
      return response.data;
    },
    enabled: enabled && !!mealId && servingSize > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    scaledServings: data,
    scaledIngredients: data?.scaledIngredientQuantities ?? [],
    isLoading,
    error,
    refetch,
  };
};
